import { extractRequestFromQueryKey } from "../../../keys";
import { removeItemFromInfiniteDataList, upsertInInfiniteDataList } from "../../../utils";
import { TellescopeModelFromName } from "../../helper-types";
import { modelMatchesGetSomeRequest } from "../filter-utils";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

export const TICKET_QUEUES_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"ticket_queues"> =
  {
    update_indexes: (request) => {
      const affectedIds = request.updates.map(({ id }) => id);

      return {
        getOne: {
          predicate: ({ queryKey }) => {
            const queryRequest = extractRequestFromQueryKey(queryKey);
            return typeof queryRequest === "string" && affectedIds.includes(queryRequest);
          },
          updater: (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              index: request.updates.find(({ id }) => id === oldData.id)?.index ?? oldData.index,
            };
          },
        },
        getSome: {
          predicate: ({ state: { data } }) => {
            if (!data) return false;
            return data.pages.some((page) => page.some((queue) => affectedIds.includes(queue.id)));
          },
          updater: (oldData, context) => {
            if (!oldData) return oldData;

            const queryRequest = context?.queryKey ? extractRequestFromQueryKey(context.queryKey) : undefined;

            let result = oldData;

            request.updates.forEach((update) => {
              const existingQueue = result.pages.reduce<TellescopeModelFromName<"ticket_queues"> | undefined>(
                (acc, page) => {
                  if (acc) return acc;
                  return page.find((queue) => queue.id === update.id);
                },
                undefined
              );

              if (!existingQueue) return;

              const updatedQueue = { ...existingQueue, index: update.index };
              const shouldKeepInList = modelMatchesGetSomeRequest(updatedQueue, queryRequest);

              if (shouldKeepInList) {
                result =
                  upsertInInfiniteDataList<TellescopeModelFromName<"ticket_queues">, unknown>({
                    infiniteData: result,
                    updater: updatedQueue,
                    matchFn: (queue) => queue.id === update.id,
                  }) ?? result;
              } else {
                result =
                  removeItemFromInfiniteDataList<TellescopeModelFromName<"ticket_queues">, unknown>({
                    infiniteData: result,
                    matchFn: (queue) => queue.id === update.id,
                  }) ?? result;
              }
            });

            return result;
          },
        },
        getByIds: {
          predicate: ({ queryKey }) => {
            const queryRequest = extractRequestFromQueryKey(queryKey);
            return !!queryRequest?.ids?.some((id) => affectedIds.includes(id));
          },
          updater: (oldData) => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              matches: oldData.matches.map((ticket) => {
                const updateMatch = request.updates.find(({ id }) => id === ticket.id);
                if (!updateMatch) return ticket;
                return {
                  ...ticket,
                  index: updateMatch.index,
                };
              }),
            };
          },
        },
      };
    },
  };
